import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Button,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    ListItemIcon,
    IconButton,
    Chip,
    Stack,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Avatar,
    Divider,
    Menu,
    MenuItem,
    Tooltip,
    Collapse,
    Badge,
    CircularProgress
} from '@mui/material';
import {
    LocationOn,
    Add,
    Edit,
    Delete,
    Security,
    Schedule,
    Phone,
    Email,
    Visibility,
    VisibilityOff,
    MoreVert,
    Map,
    DirectionsWalk,
    LocalShipping,
    Warning,
    CheckCircle,
    Search,
    FilterList,
    AccessTime,
    Business,
    ExpandMore,
    ExpandLess
} from '@mui/icons-material';
import { 
    getCustomerDropLotLocations, 
    getAllDropLotLocations, 
    deleteDropLotLocation,
    getDropLotGateCode 
} from '../services/dropLotLocationService';
import DropLotLocationForm from './DropLotLocationForm';
import { useAuth } from '../context/AuthContext';

function DropLotManagement({ customer, standalone = false }) {
    const { user } = useAuth();
    const [dropLots, setDropLots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editingDropLot, setEditingDropLot] = useState(null);
    const [gateCodeDialog, setGateCodeDialog] = useState({ open: false, dropLot: null, code: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, dropLot: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedDropLot, setSelectedDropLot] = useState(null);
    const [expandedDropLot, setExpandedDropLot] = useState(null);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        loadDropLots();
    }, [customer]);

    const loadDropLots = async () => {
        try {
            setLoading(true);
            setError(null);
            
            let response;
            if (customer) {
                response = await getCustomerDropLotLocations(customer._id);
            } else {
                response = await getAllDropLotLocations();
            }
            
            setDropLots(response.data?.data || []);
        } catch (err) {
            console.error('Error loading drop lots:', err);
            setError('Failed to load drop lot locations');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingDropLot(null);
        setFormOpen(true);
    };

    const handleEdit = (dropLot) => {
        setEditingDropLot(dropLot);
        setFormOpen(true);
        handleMenuClose();
    };

    const handleDelete = (dropLot) => {
        setDeleteDialog({ open: true, dropLot });
        handleMenuClose();
    };

    const confirmDelete = async () => {
        try {
            await deleteDropLotLocation(deleteDialog.dropLot._id);
            await loadDropLots();
            setDeleteDialog({ open: false, dropLot: null });
        } catch (err) {
            console.error('Error deleting drop lot:', err);
            setError('Failed to delete drop lot location');
        }
    };

    const handleViewGateCode = async (dropLot) => {
        try {
            const response = await getDropLotGateCode(dropLot._id);
            setGateCodeDialog({ 
                open: true, 
                dropLot, 
                code: response.data?.data?.gateCode 
            });
        } catch (err) {
            console.error('Error fetching gate code:', err);
            setError('Access denied or gate code not available');
        }
        handleMenuClose();
    };

    const handleFormSave = (savedDropLot) => {
        loadDropLots();
        setFormOpen(false);
    };

    const handleMenuOpen = (event, dropLot) => {
        setMenuAnchor(event.currentTarget);
        setSelectedDropLot(dropLot);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedDropLot(null);
    };

    const handleExpandToggle = (dropLotId) => {
        setExpandedDropLot(expandedDropLot === dropLotId ? null : dropLotId);
    };

    const filteredDropLots = dropLots.filter(dropLot =>
        dropLot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dropLot.identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dropLot.address?.city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (dropLot) => {
        if (!dropLot.isActive) return 'error';
        if (dropLot.priority === 'primary') return 'success';
        if (dropLot.priority === 'secondary') return 'warning';
        return 'default';
    };

    const formatOperatingHours = (dropLot) => {
        if (dropLot.operatingHours && dropLot.operatingHours !== '24/7') {
            return dropLot.operatingHours;
        }
        
        // Check if detailed hours are available
        if (dropLot.dayHours) {
            const hasDetailedHours = Object.values(dropLot.dayHours).some(day => day.open || day.close);
            if (hasDetailedHours) {
                return 'See detailed schedule';
            }
        }
        
        return '24/7';
    };

    const DropLotCard = ({ dropLot }) => {
        const isExpanded = expandedDropLot === dropLot._id;
        
        return (
            <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    <LocationOn />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" component="div">
                                        {dropLot.name}
                                        {dropLot.identifier && (
                                            <Chip 
                                                label={dropLot.identifier} 
                                                size="small" 
                                                sx={{ ml: 1 }} 
                                            />
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {dropLot.address?.street}, {dropLot.address?.city}, {dropLot.address?.state} {dropLot.address?.zip}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Business fontSize="small" color="action" />
                                        <Typography variant="caption">
                                            Type: {dropLot.lotType?.replace('_', ' ') || 'N/A'}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <AccessTime fontSize="small" color="action" />
                                        <Typography variant="caption">
                                            {formatOperatingHours(dropLot)}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Chip 
                                        label={dropLot.priority?.charAt(0).toUpperCase() + dropLot.priority?.slice(1) || 'Standard'}
                                        size="small"
                                        color={getStatusColor(dropLot)}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} sm={6} md={3}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Security fontSize="small" color="action" />
                                        <Typography variant="caption">
                                            {dropLot.gateCode ? 'Secured' : 'Open Access'}
                                        </Typography>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Collapse in={isExpanded}>
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" gutterBottom>Details</Typography>
                                            {dropLot.capacity?.maxTrailers && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Max Trailers: {dropLot.capacity.maxTrailers}
                                                </Typography>
                                            )}
                                            {dropLot.capacity?.maxTrucks && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Max Trucks: {dropLot.capacity.maxTrucks}
                                                </Typography>
                                            )}
                                            {dropLot.driverInstructions && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Instructions: {dropLot.driverInstructions}
                                                </Typography>
                                            )}
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="subtitle2" gutterBottom>Contact</Typography>
                                            {dropLot.contactInfo?.phone && (
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Phone fontSize="small" />
                                                    <Typography variant="body2">{dropLot.contactInfo.phone}</Typography>
                                                </Stack>
                                            )}
                                            {dropLot.contactInfo?.email && (
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Email fontSize="small" />
                                                    <Typography variant="body2">{dropLot.contactInfo.email}</Typography>
                                                </Stack>
                                            )}
                                        </Grid>
                                        
                                        {dropLot.amenities?.length > 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" gutterBottom>Amenities</Typography>
                                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                    {dropLot.amenities.map((amenity) => (
                                                        <Chip 
                                                            key={amenity} 
                                                            label={amenity.replace('_', ' ')} 
                                                            size="small" 
                                                            variant="outlined" 
                                                        />
                                                    ))}
                                                </Stack>
                                            </Grid>
                                        )}
                                        
                                        {dropLot.restrictions?.length > 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" gutterBottom>Restrictions</Typography>
                                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                    {dropLot.restrictions.map((restriction) => (
                                                        <Chip 
                                                            key={restriction} 
                                                            label={restriction.replace('_', ' ')} 
                                                            size="small" 
                                                            color="warning"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Stack>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            </Collapse>
                        </Box>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton 
                                onClick={() => handleExpandToggle(dropLot._id)}
                                size="small"
                            >
                                {isExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                            
                            {(user?.role === 'admin' || user?.role === 'company_admin') && (
                                <IconButton 
                                    onClick={(e) => handleMenuOpen(e, dropLot)}
                                    size="small"
                                >
                                    <MoreVert />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant={standalone ? "h5" : "h6"}>
                    Drop Lot Locations
                    {customer && ` for ${customer.name}`}
                    <Badge badgeContent={dropLots.length} color="primary" sx={{ ml: 2 }} />
                </Typography>
                
                {(user?.role === 'admin' || user?.role === 'company_admin') && (
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAddNew}
                    >
                        Add Drop Lot
                    </Button>
                )}
            </Box>

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search drop lots by name, identifier, or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    )
                }}
                sx={{ mb: 3 }}
            />

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Drop Lot List */}
            {filteredDropLots.length === 0 ? (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <LocationOn sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No Drop Lot Locations
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                            {customer 
                                ? `No drop lot locations have been added for ${customer.name} yet.`
                                : 'No drop lot locations found matching your search criteria.'
                            }
                        </Typography>
                        {(user?.role === 'admin' || user?.role === 'company_admin') && (
                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={handleAddNew}
                            >
                                Add First Drop Lot
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <Box>
                    {filteredDropLots.map((dropLot) => (
                        <DropLotCard key={dropLot._id} dropLot={dropLot} />
                    ))}
                </Box>
            )}

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedDropLot)}>
                    <ListItemIcon><Edit /></ListItemIcon>
                    Edit
                </MenuItem>
                {selectedDropLot?.gateCode && (
                    <MenuItem onClick={() => handleViewGateCode(selectedDropLot)}>
                        <ListItemIcon><Security /></ListItemIcon>
                        View Gate Code
                    </MenuItem>
                )}
                <MenuItem onClick={() => handleDelete(selectedDropLot)}>
                    <ListItemIcon><Delete /></ListItemIcon>
                    Deactivate
                </MenuItem>
            </Menu>

            {/* Drop Lot Form Dialog */}
            <DropLotLocationForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                dropLot={editingDropLot}
                customer={customer}
                onSave={handleFormSave}
            />

            {/* Gate Code Dialog */}
            <Dialog open={gateCodeDialog.open} onClose={() => setGateCodeDialog({ open: false, dropLot: null, code: null })}>
                <DialogTitle>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Security color="primary" />
                        <Typography variant="h6">Gate Code</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Location: {gateCodeDialog.dropLot?.name}
                    </Typography>
                    <TextField
                        fullWidth
                        label="Gate Code"
                        value={gateCodeDialog.code || 'Not available'}
                        InputProps={{
                            readOnly: true,
                            style: { fontSize: '1.2rem', fontWeight: 'bold' }
                        }}
                        sx={{ mt: 2 }}
                    />
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        This gate code access has been logged for security purposes.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGateCodeDialog({ open: false, dropLot: null, code: null })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, dropLot: null })}>
                <DialogTitle>Confirm Deactivation</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to deactivate "{deleteDialog.dropLot?.name}"? 
                        This will hide it from active lists but preserve the data for historical purposes.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, dropLot: null })}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default DropLotManagement; 